/**
 * @fileoverview Servicio de gestión de órdenes - Versión robusta con URLs absolutas
 * Implementa patrones arquitectónicos SOLID para garantizar resistencia a fallos
 * y compatibilidad entre entornos (Firebase Hosting / dominio personalizado)
 */
import api from './apiClient';
import userDataService from './userDataService';
import { buildApiUrl } from '../utils/apiUrlBuilder';
import { db } from '../config/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ordersSyncInfoConverter } from '../models/ordersSyncInfoConverter';
import TokenServiceFacade from '../infrastructure/auth/TokenServiceFacade';

/**
 * Patrón Fachada: Obtiene la lista de órdenes SIEMPRE desde Firestore
 * Esta implementación garantiza una única fuente de verdad (SSOT)
 * redirigiéndote todas las llamadas al repositorio de Firestore
 * 
 * @param {Object} options - Opciones de filtrado
 * @returns {Promise<Object>} Resultado estandarizado con órdenes
 */
export const fetchOrders = async (options = {}) => {
  // Registramos la redirección para diagnóstico
  console.log('🔄 Redirector: fetchOrders → fetchOrdersFromFirestore');
  
  try {
    // Importamos dinámicamente para evitar dependencias circulares
    const { fetchOrdersFromFirestore } = await import('./orderService.firestore');
    
    // Adaptamos los parámetros si es necesario
    const firestoreParams = {
      ...options,
      // Forzamos marca de fuente para trazabilidad
      source: 'firestore-redirected',
      // Convertimos nomenclatura API a Firestore si es necesario
      storeId: options.storeId || options.tiendaId,
      perPage: options.perPage || options.per_page
    };
    
    // Delegamos a la implementación de Firestore
    return await fetchOrdersFromFirestore(firestoreParams);
  } catch (error) {
    console.error('[orderService] Error en redirección a Firestore:', error);
    
    // Respondemos con estructura compatible vacía en caso de error
    return {
      orders: [],
      page: 1,
      perPage: options.perPage || 10,
      totalPages: 0,
      totalItems: 0,
      error: error.message,
      metadata: {
        source: 'error-redirect',
        error: true
      }
    };
  }
};

/**
 * Obtiene información sobre la última sincronización
 * @param {string} tiendaId - Identificador de tienda
 * @returns {Promise<Object>} Datos de última sincronización
 */
export const getLastSyncInfo = async (tiendaId = null) => {
  const storeId = tiendaId || userDataService.getStoreId();
  if (!storeId) {
    throw new Error('ID de tienda requerido para obtener información de sincronización');
  }
  
  try {
    // Usar API modular v9 de Firestore
    const userRef = doc(db, 'users', storeId).withConverter(ordersSyncInfoConverter);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { lastSync: null, status: 'never' };
    }
    
    const userData = userDoc.data();
    
    return {
      lastSync: userData.ordersLastSync ? userData.ordersLastSync.toDate() : null,
      status: userData.ordersSyncStatus || 'never'
    };
  } catch (error) {
    console.error('[orderService] Error obteniendo datos de sincronización:', error);
    return { lastSync: null, status: 'error', error: error.message };
  }
};

/**
 * Actualiza el estado de sincronización de órdenes
 * @param {string} tiendaId - ID de la tienda
 * @param {Object} data - Datos de sincronización
 * @returns {Promise<boolean>} - Resultado de la operación
 */
export const updateSyncStatus = async (tiendaId = null, data = {}) => {
  const storeId = tiendaId || userDataService.getStoreId();
  if (!storeId) {
    throw new Error('ID de tienda requerido para actualizar estado de sincronización');
  }
  
  try {
    // Usar API modular v9 de Firestore
    const userRef = doc(db, 'users', storeId).withConverter(ordersSyncInfoConverter);
    await updateDoc(userRef, {
      ordersSyncStatus: data.status || 'completed',
      ordersLastSync: data.timestamp || new Date(),
      ordersSyncDetails: data.details || null
    });
    return true;
  } catch (error) {
    console.error('[orderService] Error actualizando estado de sincronización:', error);
    return false;
  }
};

/**
 * Sincroniza órdenes desde APIs externas con manejo defensivo
 * @returns {Promise<Object>} Resultado de sincronización
 */
/**
 * Inicia el proceso de sincronización de órdenes
 * Implementa el patrón Command y los principios SOLID
 * 
 * @param {Object} options - Opciones de sincronización
 * @param {boolean} options.fullSync - Si se debe hacer sincronización completa
 * @returns {Promise<Object>} Resultado de la sincronización
 */
// Exportar órdenes a CSV
export const exportOrdersCSV = async (options = {}) => {
  const tokenService = new TokenServiceFacade();
  try {
    await tokenService.getToken();
    const params = {};
    if (options.startDate) params.created_at_min = options.startDate;
    if (options.endDate) params.created_at_max = options.endDate;
    if (options.status) params.status = options.status;
    if (options.minTotal) params.min_total = options.minTotal;
    if (options.maxTotal) params.max_total = options.maxTotal;
    if (options.orderBy) params.orderBy = options.orderBy;
    if (options.orderDirection) params.orderDirection = options.orderDirection;
  if (options.storeId) params.storeId = options.storeId;

    const response = await api.get(buildApiUrl('/orders/export'), { params, responseType: 'blob' });
    return response.data; // blob CSV
  } catch (error) {
    console.error('[orderService] Error exportando CSV:', error);
    if (error.response?.status === 401) tokenService.invalidateToken();
    throw error;
  }
};

// Sincronización unificada (órdenes + métricas)
export const syncAll = async (options = {}) => {
  const tokenService = new TokenServiceFacade();
  const storeId = userDataService.getStoreId();
  if (!storeId) throw new Error('No se pudo determinar storeId');
  try {
    await tokenService.getToken();
    // Temporario: usar endpoint específico hasta unificar
    const response = await api.post(buildApiUrl('/orders/sync'), {
      storeId,
      fullSync: options.fullSync || false,
    });
    return response?.data || { success: false };
  } catch (error) {
    console.error('[orderService] Error en syncAll:', error);
    if (error.response?.status === 401) tokenService.invalidateToken();
    throw error;
  }
};

export const syncOrders = async (options = {}) => {
  const tokenService = new TokenServiceFacade();
  
  try {
    // Registrar inicio de sincronización en analytics
    try {
      localStorage.setItem('nubelytics_orders_sync_start', JSON.stringify({
        timestamp: new Date().toISOString(),
        options
      }));
    } catch (e) {/* Error no crítico */}
    
    // Asegurar token actualizado usando el sistema OAuth2
    await tokenService.getToken();
    
    // Llamada a la API con el token actualizado
    const response = await api.post(buildApiUrl('/orders/sync'), {
      fullSync: options.fullSync || false,
      requestTime: new Date().toISOString()
    });
    
    // Registrar éxito en analytics
    try {
      localStorage.setItem('nubelytics_orders_sync_success', JSON.stringify({
        timestamp: new Date().toISOString(),
        responseData: {
          success: response?.data?.success,
          message: response?.data?.message
        }
      }));
    } catch (e) {/* Error no crítico */}
    
    return response?.data || { success: false, message: 'Formato de respuesta no válido' };
  } catch (error) {
    console.error('[orderService] Error sincronizando órdenes:', error);
    
    // Determinar si es un error de autenticación
    const isAuthError = error.response?.status === 401;
    
    // Si es error de autenticación, invalidar token
    if (isAuthError) {
      tokenService.invalidateToken();
    }
    
    // Log detallado para diagnóstico
    try {
      localStorage.setItem('nubelytics_orders_sync_error', JSON.stringify({
        message: error.message,
        url: error.config?.url,
        status: error.response?.status,
        isAuthError,
        timestamp: new Date().toISOString(),
        responseData: error.response?.data
      }));
    } catch (e) {/* Error no crítico */}
    
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Error al sincronizar órdenes',
      error: true,
      isAuthError
    };
  }
};
